/* eslint-disable max-lines */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Container as HomepageV2ThemeContainer } from "scmp-sdk/components/newsletter/newsletter-widget/variants/homepage-v2-theme/styles";

import { Edition } from "shared/lib/edition";
import { setFullViewportWidthStyles, styleToggle, theme } from "shared/lib/styles";

import { AdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import {
  AdSlotContainer,
  Container as AdSlotOuterContainer,
  StyledSlot,
} from "scmp-app/components/advertisement/ad-slots/ad-slot/styles";
import { applyStylesOnSlotCollapsed } from "scmp-app/components/advertisement/styles";
import { TakeoverAdSlot } from "scmp-app/components/advertisement/takeover-ad-slot";
import { AppFooter } from "scmp-app/components/app-footer";
import { Container as CommentContainer } from "scmp-app/components/article/article-comment/styles";
import { Figure } from "scmp-app/components/content/content-cover-image/styles";
import { ContentItemHomeNativeArticle } from "scmp-app/components/content/content-item-render/variants/home-native-article";
import {
  SponsorBox as NativeArticleSponsorBox,
  CoverImage as NativeCoverImage,
} from "scmp-app/components/content/content-item-render/variants/home-native-article/styles";
import { Container as HomeFlexiUnitContainer } from "scmp-app/components/content/content-item-render/variants/home-v2-flexi-unit/styles";
import { HomeV2Advertisers } from "scmp-app/components/home-v2/advertisers";
import { HomeV2AiArticleList } from "scmp-app/components/home-v2/ai-article-list";
import { AiPrimaryWidget } from "scmp-app/components/home-v2/ai-primary-widget";
import { AiSecondaryWidget } from "scmp-app/components/home-v2/ai-secondary-widget";
import { HomeV2Around } from "scmp-app/components/home-v2/around";
import { HomeV2ChinaFutureTechStoryPackage } from "scmp-app/components/home-v2/china-future-tech-story-package";
import { HomeV2FlexiUnit } from "scmp-app/components/home-v2/flexi-unit";
import { HomeV2HeroArticles } from "scmp-app/components/home-v2/hero-articles";
import { HomeV2HeroSecondaryArticles } from "scmp-app/components/home-v2/hero-secondary-articles";
import { HomeV2Latest } from "scmp-app/components/home-v2/latest";
import { HomeV2MostPopular } from "scmp-app/components/home-v2/most-popular";
import { HomeV2Multimedia } from "scmp-app/components/home-v2/multimedia";
import { HomeV2Opinion } from "scmp-app/components/home-v2/opinion";
import { HomeV2ScmpShorts } from "scmp-app/components/home-v2/scmp-shorts";
import { HomeV2SectionTop } from "scmp-app/components/home-v2/section-top";
import { HomeV2Spotlight } from "scmp-app/components/home-v2/spotlight";
import { HomeV2Subscription } from "scmp-app/components/home-v2/subscription";
import { HomeV2ThingsToDo } from "scmp-app/components/home-v2/things-to-do";
import { HomeV2TopStories } from "scmp-app/components/home-v2/top-stories";
import { AiWidgetPlaceholder } from "scmp-app/pages/home/ai-widget-fetch/placeholder";

export const StyledAdSlot = styled(AdSlot)`
  ${StyledSlot} {
    align-items: flex-start;
  }
`;

export const Container = styled.div`
  ${setFullViewportWidthStyles}

  ${CommentContainer} {
    color: #556bb4;
  }
`;

export const ContentContainer = styled.div`
  inline-size: 100%;
  margin-block-start: 20px;
  margin-inline: auto;
  padding-inline: 20px;

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 32px;
    padding-inline: 24px;
  }

  ${theme.breakpoints.up("desktop")} {
    max-inline-size: 1272px;
    margin-block-start: 24px;
    padding-inline: 32px;
  }
`;

type ContentWrapperProps = {
  $edition?: Edition;
};

export const ContentWrapper = styled.div<ContentWrapperProps>`
  display: grid;

  ${props => {
    const isHK = props.$edition === Edition.HongKong;
    return css`
      grid:
        "hero-article" min-content
        "top-stories-ads" min-content
        "hero-secondary" min-content
        "flexi-unit-container" min-content
        "top-stories" min-content
        "nl-widget" min-content
        "opinion-widget-ads" min-content
        "opinion-widget" min-content
        "latest-widget-ads" min-content
        "story-package-one-widget" min-content
        "story-package-two-widget" min-content
        "latest-widget" min-content
        "cft-widget" min-content
        "scmp-shorts" min-content
        "things-to-do-widget-ads" min-content
        "spotlight-widget" min-content
        ${styleToggle(isHK, '"things-to-do-widget" min-content')}
        "ai-widget-1" min-content
        "section-block-1" min-content
        "multimedia-widget" min-content
        "multimedia-widget-ads" min-content
        "section-block-2" min-content
        "ai-widget-2" min-content
        "subscription-widget" min-content
        "mobile-footer-ads" min-content
        "section-block-3" min-content
        "section-block-4" min-content
        "section-block-5" min-content
        "most-popular-widget" min-content
        "most-popular-widget-ads" min-content
        "advertisers-widget" min-content
        "around-widget" min-content
        / 100%;
      gap: 20px 0;

      ${theme.breakpoints.up("tablet")} {
        grid:
          "hero-article" min-content
          "hero-secondary" min-content
          "top-stories-ads" min-content
          "flexi-unit-container" min-content
          "top-stories" min-content
          "nl-widget" min-content
          "opinion-widget-ads" min-content
          "opinion-widget" min-content
          "latest-widget-ads" min-content
          "story-package-one-widget" min-content
          "story-package-two-widget" min-content
          "latest-widget" min-content
          "cft-widget" min-content
          "scmp-shorts" min-content
          "spotlight-widget" min-content
          "things-to-do-widget" min-content
          ${styleToggle(isHK, '"things-to-do-widget-ads" min-content')}
          "ai-widget-1" min-content
          "section-block-1" min-content
          "section-block-2" min-content
          "multimedia-widget" min-content
          "multimedia-widget-ads" min-content
          "ai-widget-2" min-content
          "subscription-widget" min-content
          "section-block-3" min-content
          "section-block-4" min-content
          "section-block-5" min-content
          "most-popular-widget" min-content
          "most-popular-widget-ads" min-content
          "advertisers-widget" min-content
          "around-widget" min-content
          / 100%;
        gap: 24px 20px;
      }

      ${theme.breakpoints.up("desktop")} {
        grid:
          "top-band top-band top-band" min-content
          "nl-widget divider-1 ." min-content
          "story-package-one-widget divider-1 latest-widget" min-content
          "story-package-one-widget divider-1 latest-widget-ads" min-content
          "story-package-two-widget divider-1 latest-widget-ads" min-content
          ${styleToggle(
            isHK,
            `"cft-widget divider-1 things-to-do-widget" min-content
             "scmp-shorts divider-1 things-to-do-widget" min-content`,
            '"cft-widget divider-1 things-to-do-widget-ads" min-content',
          )}
          "scmp-shorts divider-1 things-to-do-widget-ads" min-content
          "spotlight-widget divider-1 things-to-do-widget-ads" min-content
          "d-banner-ads d-banner-ads d-banner-ads" min-content
          "ai-widget-1 ai-widget-1 ai-widget-1" min-content
          "section-block-1 divider-2 multimedia-column" min-content
          "section-block-2 divider-2 multimedia-column" min-content
          "section-block-2 divider-2 multimedia-column" min-content
          "section-block-2 divider-2 ." minmax(0, auto)
          "ai-widget-2 ai-widget-2 ai-widget-2" min-content
          "subscription-widget subscription-widget subscription-widget" min-content
          "section-block-3 divider-3 most-popular-widget" min-content
          "section-block-3 divider-3 most-popular-widget-ads" min-content
          "section-block-4 divider-3 most-popular-widget-ads" min-content
          "section-block-5 divider-3 ." min-content
          "advertisers-widget advertisers-widget advertisers-widget" min-content
          "around-widget around-widget around-widget" min-content
          / minmax(0, 1fr) 1px 300px;
        gap: 32px 24px;
      }
    `;
  }}
`;

export const Divider = styled.div`
  inline-size: 0;
  block-size: 100%;
  border-inline-start: 1px #dfdfdf solid;

  ${theme.breakpoints.down("desktop")} {
    display: none;
  }
`;

export const Divider1 = styled(Divider)`
  grid-area: divider-1;
`;
export const Divider2 = styled(Divider)`
  grid-area: divider-2;
`;
export const Divider3 = styled(Divider)`
  grid-area: divider-3;
`;

export const TopStoriesAds = styled.div`
  grid-area: top-stories-ads;

  ${theme.breakpoints.only("mobile")} {
    padding-block: 12px;
  }

  ${theme.breakpoints.up("desktop")} {
    padding-block: 16px;
  }

  ${theme.breakpoints.down("desktop")} {
    ${setFullViewportWidthStyles}
  }
`;

export const OpinionWidgetAds = styled.div`
  grid-area: opinion-widget-ads;

  ${theme.breakpoints.only("mobile")} {
    padding-block: 12px;
  }

  ${theme.breakpoints.down("desktop")} {
    ${setFullViewportWidthStyles}
  }
`;

// Desktop splits the hero/top-stories content and the sidebar into two
// independent column grids so widget heights (e.g. FlexiUnitWidget) never
// inflate each other. Below desktop the wrappers are display: contents and
// every child stays a direct grid item of ContentWrapper, as before.
export const TopBand = styled.div`
  grid-area: top-band;

  display: contents;

  ${theme.breakpoints.up("desktop")} {
    display: grid;
    grid:
      "top-band-left top-band-divider top-band-right" min-content
      / minmax(0, 1fr) 1px 300px;
    gap: 0 24px;
  }
`;

export const TopBandLeft = styled.div`
  grid-area: top-band-left;

  display: contents;

  ${theme.breakpoints.up("desktop")} {
    display: grid;
    grid:
      "hero-article" min-content
      "hero-secondary" min-content
      "top-stories" min-content
      / minmax(0, 1fr);
    gap: 32px 0;
  }
`;

export const TopBandDivider = styled(Divider)`
  grid-area: top-band-divider;
`;

export const TopBandRight = styled.div`
  grid-area: top-band-right;

  display: contents;

  ${theme.breakpoints.up("desktop")} {
    display: grid;

    /* Bottom-anchored spacer rows so the sidebar ads sit next to the same
       top stories they aligned with in the previous shared-grid layout:
       the 1fr spacer pushes both ads down to the end of the top band, and
       the fixed 432px row (tuned to top stories 13-14) keeps
       desktop-sidebar-ads-top level with top-story-12 while
       desktop-sidebar-ads-bottom rests beside top-story-15. */
    grid:
      "flexi-unit-container" min-content
      "top-stories-ads" min-content
      "opinion-widget" min-content
      "opinion-widget-ads" min-content
      "." minmax(0, 1fr)
      "desktop-sidebar-ads-top" min-content
      "." 432px
      "desktop-sidebar-ads-bottom" min-content
      / minmax(0, 1fr);
    gap: 32px 0;
  }
`;

export const DesktopSidebarAdsTop = styled.div`
  grid-area: desktop-sidebar-ads-top;

  ${theme.breakpoints.down("desktop")} {
    display: none;
  }
`;

export const DesktopSidebarAdsBottom = styled.div`
  grid-area: desktop-sidebar-ads-bottom;

  ${theme.breakpoints.down("desktop")} {
    display: none;
  }
`;

export const LatestWidgetAds = styled.div`
  grid-area: latest-widget-ads;

  ${theme.breakpoints.only("mobile")} {
    padding-block: 12px;
  }

  ${theme.breakpoints.down("desktop")} {
    ${setFullViewportWidthStyles}
  }
`;

export const ThingsToDoWidgetAds = styled.div`
  grid-area: things-to-do-widget-ads;

  ${theme.breakpoints.only("mobile")} {
    padding-block: 12px;
  }

  ${theme.breakpoints.down("desktop")} {
    ${setFullViewportWidthStyles}
  }
`;

export const MultimediaWidgetAds = styled.div`
  grid-area: multimedia-widget-ads;

  ${theme.breakpoints.only("mobile")} {
    padding-block: 12px;
  }

  ${theme.breakpoints.down("desktop")} {
    ${setFullViewportWidthStyles}
  }
`;

export const MostPopularWidgetAds = styled.div`
  grid-area: most-popular-widget-ads;

  ${theme.breakpoints.only("mobile")} {
    padding-block: 12px;
  }

  ${theme.breakpoints.down("desktop")} {
    ${setFullViewportWidthStyles}
  }
`;

export const MobileFooterAds = styled.div`
  grid-area: mobile-footer-ads;

  ${setFullViewportWidthStyles}

  padding-block: 12px;
  padding-inline: 20px;

  ${theme.breakpoints.up("tablet")} {
    display: none;
  }
`;

export const DesktopBannerAds = styled.div`
  grid-area: d-banner-ads;

  ${theme.breakpoints.down("desktop")} {
    display: none;
  }

  ${theme.breakpoints.up("desktop")} {
    ${setFullViewportWidthStyles}
    ${applyStylesOnSlotCollapsed()}
    display: flex;
    justify-content: center;
    align-items: center;

    border-block: 1px solid #dfdfdf;

    ${AdSlotOuterContainer} {
      padding-block: 20px;
    }

    ${StyledSlot} {
      align-items: center !important;
    }
  }
`;

export const HeroArticle = styled(HomeV2HeroArticles)`
  grid-area: hero-article;
`;

export const HeroSecondaryArticles = styled(HomeV2HeroSecondaryArticles)`
  grid-area: hero-secondary;

  ${theme.breakpoints.up("tablet")} {
    border-block-start: 1px #000000 solid;

    padding-block-start: 24px;
  }
`;

export const TopStories = styled(HomeV2TopStories)`
  grid-area: top-stories;
`;

export const NewsletterWidget = styled.div`
  grid-area: nl-widget;

  ${HomepageV2ThemeContainer} {
    padding-block: 12px;

    ${theme.breakpoints.only("mobile")} {
      border-block-end: 1px solid #727272;
    }

    ${theme.breakpoints.up("tablet")} {
      padding-block-start: 24px;
    }
  }
`;

export const FlexiUnitWidget = styled(HomeV2FlexiUnit)``;

export const PremiumAds = styled.div`
  ${theme.breakpoints.only("mobile")} {
    padding-block: 12px;
  }

  ${theme.breakpoints.up("tablet")} {
    min-inline-size: 300px;
    max-inline-size: 300px;
  }

  ${theme.breakpoints.up("desktop")} {
    padding-block-start: 32px;
    border-block-start: 1px solid #dfdfdf;
  }
`;

export const FlexiUnitContainer = styled.div`
  grid-area: flexi-unit-container;

  display: flex;
  flex-flow: column nowrap;
  gap: 20px;
  border-block-start: 1px solid #000000;

  padding-block-start: 12px;

  ${theme.breakpoints.only("tablet")} {
    flex-flow: row nowrap;

    padding-block-start: 24px;

    :not(:has(${PremiumAds})) {
      ${HomeFlexiUnitContainer} {
        grid-template-columns: 0.4fr 0.6fr;
      }
    }
  }

  ${theme.breakpoints.up("desktop")} {
    gap: 16px;
    border-block-start: none;

    padding-block-start: 0;
  }
`;

export const OpinionWidget = styled(HomeV2Opinion)`
  grid-area: opinion-widget;
`;

export const LatestWidget = styled(HomeV2Latest)`
  grid-area: latest-widget;
`;

export const ThingsToDoWidget = styled(HomeV2ThingsToDo)`
  grid-area: things-to-do-widget;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const CFTWidget = styled(HomeV2ChinaFutureTechStoryPackage)`
  grid-area: cft-widget;
`;

export const SCMPShortsWidget = styled(HomeV2ScmpShorts)`
  grid-area: scmp-shorts;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }

  ${theme.breakpoints.up("tablet")} {
    margin-block-end: 8px;
  }
`;

export const SpotlightWidget = styled(HomeV2Spotlight)`
  grid-area: spotlight-widget;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const AIWidget1 = styled(HomeV2AiArticleList)`
  grid-area: ai-widget-1;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const AIWidget2 = styled(HomeV2AiArticleList)`
  grid-area: ai-widget-2;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const SubscriptionWidget = styled(HomeV2Subscription)`
  grid-area: subscription-widget;

  ${theme.breakpoints.only("mobile")} {
    margin-block: 12px;
  }
`;

export const MultimediaColumn = styled.div`
  display: contents;

  ${theme.breakpoints.up("desktop")} {
    grid-area: multimedia-column;

    display: grid;
    grid-template-areas:
      "multimedia-widget"
      "multimedia-widget-ads"
      ".";
    grid-template-rows: min-content min-content 1fr;
    row-gap: 32px;
  }
`;

export const MultimediaWidget = styled(HomeV2Multimedia)`
  grid-area: multimedia-widget;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const MostPopularWidget = styled(HomeV2MostPopular)`
  grid-area: most-popular-widget;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const SectionBlock1 = styled(HomeV2SectionTop)`
  grid-area: section-block-1;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const SectionBlock2 = styled(HomeV2SectionTop)`
  grid-area: section-block-2;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const SectionBlock3 = styled(HomeV2SectionTop)`
  grid-area: section-block-3;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const SectionBlock4 = styled(HomeV2SectionTop)`
  grid-area: section-block-4;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const SectionBlock5 = styled(HomeV2SectionTop)`
  grid-area: section-block-5;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const AdvertisersWidget = styled(HomeV2Advertisers)`
  grid-area: advertisers-widget;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const AroundWidget = styled(HomeV2Around)`
  grid-area: around-widget;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const StyledAppFooter = styled(AppFooter)`
  margin-block-start: 20px;

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 32px;
  }
`;

export const StyledTakeoverAdSlot = styled(TakeoverAdSlot)`
  && ${AdSlotContainer} {
    inline-size: 100%;
  }

  && ${StyledSlot} > div {
    display: flex;
    flex: 1;
    justify-content: center;
  }

  ${theme.breakpoints.only("tablet")} {
    display: none;
  }
`;

export const StyledContentItemHomeNativeArticle = styled(ContentItemHomeNativeArticle)`
  ${NativeArticleSponsorBox} {
    gap: 2px;
  }

  ${NativeCoverImage} {
    align-self: start;

    ${Figure} {
      inline-size: 100%;
      block-size: auto;
      aspect-ratio: 3 / 2;
    }
  }
`;

export const StoryPackageOneWidgetPlaceholder = styled(AiWidgetPlaceholder)`
  grid-area: story-package-one-widget;
`;

export const StoryPackageTwoWidgetPlaceholder = styled(AiWidgetPlaceholder)`
  grid-area: story-package-two-widget;
`;

export const CFTWidgetPlaceholder = styled(AiWidgetPlaceholder)`
  grid-area: cft-widget;
`;

export const SpotlightWidgetPlaceholder = styled(AiWidgetPlaceholder)`
  grid-area: spotlight-widget;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;

export const AiStoryPackageOneWidgetWrapper = styled(AiPrimaryWidget)`
  grid-area: story-package-one-widget;
`;

export const AiStoryPackageTwoWidgetWrapper = styled(AiSecondaryWidget)`
  grid-area: story-package-two-widget;
`;

export const AiCftWidgetWrapper = styled(AiSecondaryWidget)`
  grid-area: cft-widget;
`;

export const AiSpotlightWidgetWrapper = styled(AiSecondaryWidget)`
  grid-area: spotlight-widget;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 12px;
  }
`;
